import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Table, Typography, Modal, Button, Input, DatePicker, Select } from "antd";
import ViewOneEmployeeRecord from "../hr/ViewOneEmployeeRecord";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function WeekOffCancel() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const { authData, backUrl } = useContext(AuthContext);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showSingle, setShowSingle] = useState(false);
    const [showRejectReason, setShowRejectReason] = useState({ status: false, data: null });
    const [showAcceptReason, setShowAcceptReason] = useState({ status: false, data: null });
    const [rejectionData, setRejectionData] = useState("");
    const [approvalData, setApprovalData] = useState("");

    // Filters
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [empNameFilter, setEmpNameFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState([]);
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("");

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setData(res.data.results || []);
            setFilteredData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching compoff data:", error);
            toast.error("Failed to fetch compoff data");
        }
    };

    const applyFilters = () => {
        let temp = [...data];

        if (empCodeFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.username?.toLowerCase().includes(empCodeFilter.toLowerCase())
            );
        }

        if (empNameFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.first_name?.toLowerCase().includes(empNameFilter.toLowerCase())
            );
        }

        if (dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            temp = temp.filter(item => {
                const workDate = dayjs(item.work_date);
                return (
                    workDate.isAfter(dayjs(start).startOf("day").subtract(1, "ms")) &&
                    workDate.isBefore(dayjs(end).endOf("day").add(1, "ms"))
                );
            });
        }

        if (approvalStatusFilter) {
            temp = temp.filter(item => {
                const myApproval = item.approvals?.find(a => a.approver?.id === authData?.employee?.id);
                if (!myApproval) return false;
                return myApproval.status.toLowerCase() === approvalStatusFilter.toLowerCase();
            });
        }

        setFilteredData(temp);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [empCodeFilter, empNameFilter, dateRangeFilter, approvalStatusFilter, data]);

    const handleApprove = async (id) => {
        try {
            await axios.post(`${backUrl}/api/hr/compoffs/${id}/approve-work/`, { rejection_reason: approvalData }, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            toast.success("Weekoff Approved");
            fetchData();
        } catch (error) {
            const backendMessage = error.response?.data?.detail || error.response?.data?.error || JSON.stringify(error.response?.data);
            toast.error(`Approval failed: ${backendMessage}`);
        }
    };

    const handleReject = async (id) => {
        try {
            await axios.post(`${backUrl}/api/hr/compoffs/${id}/reject-work/`, { rejection_reason: rejectionData }, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setRejectionData("");
            setShowRejectReason({ status: false, data: null });
            toast.success("Weekoff Rejected");
            fetchData();
        } catch (error) {
            const backendMessage = error.response?.data?.detail || error.response?.data?.error || JSON.stringify(error.response?.data);
            toast.error(`Rejection failed: ${backendMessage}`);
        }
    };

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "user", "username"],
            key: "employee_code",
            render: (text, record) => (
                <a onClick={() => { setSelectedEmployee(record); setShowSingle(true); }}>{text}</a>
            ),
            fixed: "left",
            width: 130,
        },
        {
            title: "Employee Name",
            key: "employee_name",
            render: (record) => {
                const firstName = record?.employee?.user?.first_name || "";
                const lastName = record?.employee?.user?.last_name || "";
                return `${firstName} ${lastName}`.trim() || "-";
            },
            width: 160,
        },
        { title: "Work Date", dataIndex: "work_date", key: "work_date", width: 140 },
        { title: "Expiry Date", dataIndex: "expiry_date", key: "expiry_date", width: 140 },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: 300,
            render: text => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "280px" }}>{text}</div>
            ),
        },
        {
            title: "Approval Status",
            key: "approval_status",
            width: 150,
            render: (_, record) => {
                const myApproval = record.approvals?.find(a => a.approver?.id === authData?.employee?.id);
                return (
                    <span style={{ fontWeight: 500, color: myApproval?.status === "Approved" ? "green" : myApproval?.status === "Rejected" ? "red" : "#faad14" }}>
                        {myApproval ? myApproval.status : "N/A"}
                    </span>
                );
            },
        },
        {
            title: "Approval Reason",
            key: "rejection_reason",
            width: 300,
            render: (_, record) => {
                const rejectedApprovals = record.approvals;
                if (!rejectedApprovals || rejectedApprovals.length === 0) return "-";
                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "280px" }}>
                        {rejectedApprovals.map((a, index) => (
                            a.rejection_reason ? (
                                <div key={index}>
                                    <strong>
                                        {a.approver?.user?.first_name && a.approver?.user?.last_name
                                            ? `${a.approver.user.first_name} ${a.approver.user.last_name}`
                                            : a.approver?.user?.username}
                                        :
                                    </strong>{" "}
                                    {a.rejection_reason}
                                </div>
                            ) : null
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Approve",
            key: "approve",
            
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                    onClick={() => setShowAcceptReason({ status: true, data: record.id })}
                />
            ),
        },
        {
            title: "Reject",
            key: "reject",
            
            width: 100,
            render: (_, record) => (
                <Button
                    danger
                    type="primary"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={() => setShowRejectReason({ status: true, data: record.id })}
                />
            ),
        },
    ];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <center><Title level={5}>Team Off Day Working</Title></center>

            {/* Filters */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                <Input placeholder="Employee Code" value={empCodeFilter} onChange={e => setEmpCodeFilter(e.target.value)} style={{ width: 180 }} />
                <Input placeholder="Employee Name" value={empNameFilter} onChange={e => setEmpNameFilter(e.target.value)} style={{ width: 180 }} />
                <RangePicker value={dateRangeFilter} onChange={dates => setDateRangeFilter(dates || [])} style={{ width: 250 }} />
                <Select placeholder="Approval Status" value={approvalStatusFilter || undefined} onChange={value => setApprovalStatusFilter(value)} allowClear style={{ width: 180 }}>
                    <Option value="Approved">Approved</Option>
                    <Option value="Rejected">Rejected</Option>
                    <Option value="Pending">Pending</Option>
                </Select>
            </div>

            {/* ✅ Scrollable Table */}
            <div style={{ overflowX: "auto" }}>
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    scroll={{ x: 1200 }}  // 👈 enables horizontal scroll
                    components={{
                        header: { cell: props => <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>{props.children}</th> }
                    }}
                />
            </div>

            {/* Reject Modal */}
            <Modal title="Reason for Rejection" open={showRejectReason.status} footer={null} onCancel={() => setShowRejectReason({ status: false, data: null })} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter reason here..." value={rejectionData} onChange={e => setRejectionData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowRejectReason({ status: false, data: null })}>Cancel</Button>
                    <Button type="primary" danger onClick={() => handleReject(showRejectReason.data)}>Reject</Button>
                </div>
            </Modal>

            {/* Approval Modal */}
            <Modal title="Approval Remarks" open={showAcceptReason.status} footer={null} onCancel={() => setShowAcceptReason({ status: false, data: null })} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter remarks here..." value={approvalData} onChange={e => setApprovalData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowAcceptReason({ status: false, data: null })}>Cancel</Button>
                    <Button type="primary" style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }} onClick={() => handleApprove(showAcceptReason.data)}>Approve</Button>
                </div>
            </Modal>

            {/* Employee Record Modal */}
            <Modal open={showSingle} footer={null} onCancel={() => setShowSingle(false)} width={1300}>
                <ViewOneEmployeeRecord employee={selectedEmployee} />
            </Modal>
        </>
    );
}
